import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    approveObjectifRequest,
    createObjectifRequest,
    fetchObjectifsNRequest,
    rejectObjectifRequest,
    updateObjectifRequest,
} from "../../../../redux/actions/performanceActions";
import { asArray, StepState } from "../../Collaborateur/performanceUi";
import ObjectifsAIAssistanceModal, { buildObjectifsAiStats } from "../../Collaborateur/steps/ObjectifsAIAssistanceModal";
import ObjectifActionModal from "../../components/ObjectifActionModal";
import ObjectifNCreationForm from "../../Collaborateur/steps/ObjectifNCreationForm";
import "./ObjectifsNManagerStep.css";

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("fr-FR");
};

const formatPercent = (value) => value === undefined || value === null || value === "" ? "-" : `${value}%`;

export default function ObjectifsNManagerStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.objectifsN);
    const data = slice.dataByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const objectifs = asArray(data);
    const stepper = useSelector((state) => state.performance.progress.stepperByProgressId[String(progressId)]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [decision, setDecision] = useState(null);
    const [comment, setComment] = useState("");
    const [aiModalOpen, setAiModalOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchObjectifsNRequest(progressId));
    }, [dispatch, progressId]);

    const aiStats = useMemo(() => buildObjectifsAiStats(objectifs, stepper), [objectifs, stepper]);
    const totalPoids = objectifs.reduce((sum, objectif) => sum + (Number(objectif.poids) || 0), 0);
    const editingObjectif = objectifs.find((objectif) => objectif.id === editId);
    const decidingObjectif = objectifs.find((objectif) => objectif.id === decision?.objectifId);

    const openEdit = (objectif) => {
        setEditId(objectif.id);
        setEditForm({
            titre: objectif.titre || "",
            description: objectif.description || "",
            indicateur: objectif.indicateur || "",
            typeIndicateur: objectif.typeIndicateur || "",
            typeObjectif: objectif.typeObjectif || "QUANTITATIF",
            priorite: objectif.priorite ?? "",
            dateFin: objectif.dateFin || "",
            poids: objectif.poids ?? "",
            performanceCible: objectif.performanceCible ?? "",
            surperformance: objectif.surperformance ?? "",
            tolerance: objectif.tolerance ?? "",
        });
    };

    const saveEdit = () => {
        if (!editingObjectif) return;
        dispatch(updateObjectifRequest(editingObjectif.id, {
            ...editForm,
            evaluationProgressId: Number(progressId),
            collaborateurId: editingObjectif.collaborateurId,
        }, progressId));
        setEditId(null);
    };

    const decide = () => {
        if (!decidingObjectif) return;
        const action = decision.type === "approve" ? approveObjectifRequest : rejectObjectifRequest;
        dispatch(action(decidingObjectif.id, {
            evaluationProgressId: Number(progressId),
            commentaire: comment,
        }));
        setDecision(null);
        setComment("");
    };

    return (
        <StepState loading={loading} error={error} empty={false}>
            <div className="on-page on-manager-page">
                <div className="on-header">
                    <div className="on-header__left">
                        <h2 className="on-header__title">Objectifs N</h2>
                    </div>
                    <div className="on-header__stats">
                        <div className="on-stat on-stat--blue"><span className="on-stat__value">{objectifs.length}</span><span className="on-stat__label">Objectifs</span></div>
                        <div className={`on-stat ${totalPoids > 100 ? "on-stat--red" : totalPoids === 100 ? "on-stat--green" : "on-stat--orange"}`}><span className="on-stat__value">{totalPoids}%</span><span className="on-stat__label">Poids</span></div>
                    </div>
                </div>

                <div className="on-list-header">
                    <h3 className="on-list-title">Liste des objectifs</h3>
                    <div className="on-objectives-actions">
                        <button className="on-btn on-btn--ai" type="button" onClick={() => setAiModalOpen(true)}>Assistance IA</button>
                        <button className="on-btn on-btn--primary" type="button" onClick={() => setCreateOpen(true)}>+ Ajouter</button>
                    </div>
                </div>

                <div className="on-objectives-table-wrap">
                    <table className="on-objectives-table">
                        <thead>
                            <tr>
                                <th>Titre</th>
                                <th>Type</th>
                                <th>Indicateur</th>
                                <th>Performance cible</th>
                                <th>Surperformance</th>
                                <th>Poids</th>
                                <th>Échéance</th>
                                <th>Statut approbation</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {objectifs.map((objectif) => {
                                const actions = objectif.actions || {};
                                return (
                                    <tr key={objectif.id}>
                                        <td>
                                            <strong className="on-objectives-title">{objectif.titre}</strong>
                                            {objectif.description && <span className="on-objectives-description">{objectif.description}</span>}
                                        </td>
                                        <td>{objectif.typeObjectif || "-"}</td>
                                        <td>{objectif.indicateur || "-"}</td>
                                        <td>{formatPercent(objectif.performanceCible)}</td>
                                        <td>{formatPercent(objectif.surperformance)}</td>
                                        <td>{formatPercent(objectif.poids)}</td>
                                        <td>{formatDate(objectif.dateFin)}</td>
                                        <td><span className={`on-status-badge on-status-badge--${String(objectif.approvalStatus || "").toLowerCase()}`}>{objectif.approvalStatus || "-"}</span></td>
                                        <td>
                                            <div className="on-objectives-actions">
                                                {actions.canApprove && <button className="on-btn on-btn--primary on-btn--xs" type="button" onClick={() => setDecision({ type: "approve", objectifId: objectif.id })}>Approuver</button>}
                                                {actions.canReject && <button className="on-btn on-btn--danger on-btn--xs" type="button" onClick={() => setDecision({ type: "reject", objectifId: objectif.id })}>Rejeter</button>}
                                                {actions.canModify && <button className="on-btn on-btn--outline on-btn--xs" type="button" onClick={() => openEdit(objectif)}>Modifier</button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {objectifs.length === 0 && (
                                <tr>
                                    <td colSpan={9}><div className="on-empty"><p>Aucun objectif N pour le moment.</p></div></td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ObjectifActionModal open={createOpen} title="Nouvel objectif N" onClose={() => setCreateOpen(false)}>
                <ObjectifNCreationForm onSubmit={(payload) => {
                    dispatch(createObjectifRequest({ ...payload, evaluationProgressId: Number(progressId) }));
                    setCreateOpen(false);
                }} onCancel={() => setCreateOpen(false)} />
            </ObjectifActionModal>

            <ObjectifActionModal open={Boolean(editingObjectif)} title="Modifier l'objectif N" onClose={() => setEditId(null)} footer={<><button className="on-btn on-btn--outline" type="button" onClick={() => setEditId(null)}>Annuler</button><button className="on-btn on-btn--primary" type="button" onClick={saveEdit}>Enregistrer</button></>}>
                <div className="on-form on-form--modal">
                    <div className="on-form__row on-form__row--2">
                        <div className="on-field"><label className="on-field__label">Titre</label><input className="on-input" value={editForm.titre || ""} onChange={(event) => setEditForm((current) => ({ ...current, titre: event.target.value }))} /></div>
                        <div className="on-field"><label className="on-field__label">Priorité</label><input className="on-input" type="number" value={editForm.priorite || ""} onChange={(event) => setEditForm((current) => ({ ...current, priorite: event.target.value }))} /></div>
                    </div>
                    <div className="on-field"><label className="on-field__label">Description</label><textarea className="on-textarea" rows={4} value={editForm.description || ""} onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))} /></div>
                    <div className="on-form__row on-form__row--2">
                        <div className="on-field"><label className="on-field__label">Indicateur</label><input className="on-input" value={editForm.indicateur || ""} onChange={(event) => setEditForm((current) => ({ ...current, indicateur: event.target.value }))} /></div>
                        <div className="on-field"><label className="on-field__label">Type d'indicateur</label><input className="on-input" value={editForm.typeIndicateur || ""} onChange={(event) => setEditForm((current) => ({ ...current, typeIndicateur: event.target.value }))} /></div>
                    </div>
                    <div className="on-form__row on-form__row--4">
                        <div className="on-field"><label className="on-field__label">Date d'échéance</label><input className="on-input" type="date" value={editForm.dateFin || ""} onChange={(event) => setEditForm((current) => ({ ...current, dateFin: event.target.value }))} /></div>
                        <div className="on-field"><label className="on-field__label">Poids</label><input className="on-input" type="number" value={editForm.poids || ""} onChange={(event) => setEditForm((current) => ({ ...current, poids: event.target.value }))} /></div>
                        <div className="on-field"><label className="on-field__label">Performance cible</label><input className="on-input" type="number" value={editForm.performanceCible || ""} onChange={(event) => setEditForm((current) => ({ ...current, performanceCible: event.target.value }))} /></div>
                        <div className="on-field"><label className="on-field__label">Surperformance</label><input className="on-input" type="number" value={editForm.surperformance || ""} onChange={(event) => setEditForm((current) => ({ ...current, surperformance: event.target.value }))} /></div>
                    </div>
                    <div className="on-field"><label className="on-field__label">Tolérance</label><input className="on-input" type="number" value={editForm.tolerance || ""} onChange={(event) => setEditForm((current) => ({ ...current, tolerance: event.target.value }))} /></div>
                </div>
            </ObjectifActionModal>

            <ObjectifActionModal open={Boolean(decision)} title={decision?.type === "approve" ? "Approuver l'objectif" : "Rejeter l'objectif"} onClose={() => setDecision(null)} footer={<><button className="on-btn on-btn--outline" type="button" onClick={() => setDecision(null)}>Annuler</button><button className={`on-btn ${decision?.type === "approve" ? "on-btn--primary" : "on-btn--danger"}`} type="button" onClick={decide}>{decision?.type === "approve" ? "Approuver" : "Rejeter"}</button></>}>
                <div className="on-field">
                    <label className="on-field__label">Commentaire manager</label>
                    <textarea className="on-textarea" rows={4} value={comment} onChange={(event) => setComment(event.target.value)} />
                </div>
            </ObjectifActionModal>

            <ObjectifsAIAssistanceModal open={aiModalOpen} onClose={() => setAiModalOpen(false)} stats={aiStats} onGenerate={() => Swal.fire({ icon: "info", text: "La configuration IA est prête, sans données mock." })} />
        </StepState>
    );
}
